import React from 'react';
import { FileText, Image, Presentation, Palette, X, CheckCircle, AlertCircle } from 'lucide-react';

const ImportProgressDialog = ({ 
  isOpen, 
  onClose, 
  importType = 'pdf', 
  fileName = '', 
  status = 'processing', // 'processing', 'success', 'error'
  progress = 0,
  message = '',
  allowCancel = false 
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (importType) {
      case 'pdf':
        return <FileText className="w-8 h-8 text-red-600" />;
      case 'psd':
        return <Image className="w-8 h-8 text-blue-600" />;
      case 'powerpoint':
        return <Presentation className="w-8 h-8 text-orange-600" />;
      case 'cdr':
        return <Palette className="w-8 h-8 text-purple-600" />;
      default:
        return <FileText className="w-8 h-8 text-gray-600" />;
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-6 h-6 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  const getProgressColor = () => {
    switch (status) {
      case 'success':
        return 'bg-green-600';
      case 'error':
        return 'bg-red-600';
      default:
        return 'bg-blue-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {getIcon()}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Importing {importType.toUpperCase()}
              </h2>
              <p className="text-sm text-gray-600 truncate max-w-xs">
                {fileName}
              </p>
            </div>
          </div>
          {(allowCancel || status === 'success' || status === 'error') && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Status */}
          <div className="flex items-center space-x-3 mb-4">
            {status === 'processing' ? (
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              getStatusIcon()
            )}
            <span className={`font-medium ${getStatusColor()}`}>
              {status === 'processing' && 'Processing file...'}
              {status === 'success' && 'Import completed successfully!'}
              {status === 'error' && 'Import failed'}
            </span>
          </div>

          {/* Progress Bar */}
          {status === 'processing' && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progress</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
                  style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Message */}
          <div className="text-sm text-gray-600 mb-6">
            {status === 'processing' && (
              <div className="space-y-2">
                <p>{message || 'Please wait while we process your file...'}</p>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-blue-700">
                    <strong>💡 Processing large files:</strong>
                  </p>
                  <ul className="text-xs text-blue-600 mt-1 space-y-1">
                    <li>• Extracting images and graphics</li>
                    <li>• Converting text and shapes</li>
                    <li>• Uploading assets to cloud storage</li>
                    <li>• This may take several minutes for large files</li>
                  </ul>
                </div>
              </div>
            )}
            {status === 'success' && (
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-green-700 text-sm">
                  {message || 'Your file has been successfully imported and is ready to use.'}
                </p>
              </div>
            )}
            {status === 'error' && (
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-red-700 text-sm">
                  {message || 'An error occurred while importing your file. Please try again.'}
                </p>
                <div className="mt-2 text-xs text-red-600">
                  <p><strong>Common solutions:</strong></p>
                  <ul className="mt-1 space-y-1">
                    <li>• Check your internet connection</li>
                    <li>• Ensure the file is not corrupted</li>
                    <li>• Try with a smaller file size</li>
                    <li>• Contact support if the issue persists</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            {status === 'processing' && allowCancel && (
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
            )}
            {(status === 'success' || status === 'error') && (
              <button
                onClick={onClose}
                className={`px-6 py-2 rounded-lg text-white transition-colors ${
                  status === 'success' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {status === 'success' ? 'Continue' : 'Close'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportProgressDialog;
